import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Dropdown, Menu } from "antd";
import { MoreOutlined } from "@ant-design/icons";

import { AppState } from "store";
import { Table, TablePagination } from "components/base";
import { parseVehicleModelTable } from "utils/parsers";
import {
  initializeVehicleModel,
  setIssuesSelectedPage,
  setVehicleModels,
  setSelectedVehicleModel,
} from "store/actions";
import { ActionMenu, issueTypeCell, statusCell } from "./cell-renders";
import { TableProps } from "antd/lib/table";

// Wrapper component for ActionMenu with proper spacing
const ActionMenuWrapper = (value: any, record: any, index: number) => {
  return (
    <div className="action-menu-wrapper">
      {ActionMenu(value, record, index)}
    </div>
  );
};

const columns: TableProps<any>["columns"] = [
  {
    title: "Model",
    dataIndex: "name",
    width: 150,
    ellipsis: true,
  },
  {
    title: "Make",
    dataIndex: "make",
    width: 130,
    ellipsis: true,
  },
  {
    title: "Year",
    dataIndex: "year",
    width: 130,
    ellipsis: true,
  },
  {
    title: "Model Type",
    dataIndex: "type",
    width: 130,
    ellipsis: true,
  },
  {
    title: "Firmware version",
    dataIndex: "firmware_version",
    width: 150,
    ellipsis: true,
  },
  {
    title: "",
    dataIndex: "action",
    render: ActionMenuWrapper, // Use wrapper instead of direct ActionMenu
    align: "right",
    className: "action-menu open-link --issues",
    width: 120, // Added width for action column
  },
];

// Mobile card component
function MobileModelCard({ item, index }: { item: any; index: number }) {
  return (
    <Card
      className="mobile-model-card"
      bordered={false}
      style={{
        marginBottom: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        borderRadius: 8,
      }}
    >
      <div className="mobile-card-header">
        <div className="mobile-card-title">
          <h3>{item.name}</h3>
          <span className="mobile-card-subtitle">{item.make}</span>
        </div>
        <div className="mobile-card-action">
          <div className="action-menu-wrapper">
            {ActionMenu(item.action, item, index)}
          </div>
        </div>
      </div>
      <div className="mobile-card-content">
        <div className="mobile-card-row">
          <span className="mobile-card-label">Year:</span>
          <span className="mobile-card-value">{item.year}</span>
        </div>
        <div className="mobile-card-row">
          <span className="mobile-card-label">Model Type:</span>
          <span className="mobile-card-value">{item.type}</span>
        </div>
        <div className="mobile-card-row">
          <span className="mobile-card-label">Firmware:</span>
          <span className="mobile-card-value">{item.firmware_version}</span>
        </div>
      </div>
    </Card>
  );
}

export default function ModelTableView() {
  const dispatch = useDispatch();
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const {
    models,
    page,
    search,
    selectedModelsRows,
    filters: { listType },
  } = useSelector<AppState, AppState["models"]>((state) => state.models);
  const { selectedPage, totalPages } = page;

  const data = useMemo(
    () => parseVehicleModelTable(search, models, listType),
    [models, listType, search]
  );

  // Responsive breakpoint detection
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    dispatch(initializeVehicleModel());
  }, [dispatch]);

  console.log("Data in models : = ", data);

  const onChangeSelection = useCallback(
    (value) => {
      dispatch(setSelectedVehicleModel(value));
    },
    [dispatch]
  );

  const onBack = useCallback(() => {
    if (selectedPage > 1) {
      dispatch(setIssuesSelectedPage(selectedPage - 1));
    }
  }, [dispatch, selectedPage]);

  const onNext = useCallback(() => {
    if (selectedPage < totalPages) {
      dispatch(setIssuesSelectedPage(selectedPage + 1));
    }
  }, [dispatch, selectedPage, totalPages]);

  const resetCallback = useCallback(() => {
    dispatch(setIssuesSelectedPage(1));
  }, [dispatch]);

  // Tablet columns (hide some columns)
  const tabletColumns = useMemo(
    () =>
      (columns || []).filter((col) => {
        if ("dataIndex" in col) {
          const dataIndex = (col.dataIndex as string) || "";
          return !["firmware_version"].includes(dataIndex);
        }
        return true;
      }),
    [columns]
  );

  return (
    <>
      <div className="responsive-table-container">
        {isMobile ? (
          <div className="mobile-cards-wrapper">
            {data.length > 0 ? (
              data.map((item, index) => (
                <MobileModelCard key={item.key || index} item={item} index={index} />
              ))
            ) : (
              <div className="empty-state">No models found</div>
            )}
          </div>
        ) : (
          <Table
            columns={isTablet ? tabletColumns : columns}
            dataSource={data}
            pagination={false}
         //   scroll={{ y: isMobile ? "calc(100vh - 200px)" : "70vh", x: isTablet ? 800 : undefined }}
            className="responsive-model-table"
          />
        )}
      </div>

      <style>{`
        .responsive-table-container {
          width: 100%;
          overflow: hidden;
        }

        /* ============================================
           ACTION MENU WRAPPER STYLES - KEY SPACING
           ============================================ */
        
        .action-menu-wrapper {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: flex-end !important;
          gap: 16px !important; /* Main spacing between Edit and Actions */
        }

        

        .action-menu-wrapper > * {
          margin: 0 !important;
        }

        /* Target all child elements within action menu wrapper */
        .action-menu-wrapper button,
        .action-menu-wrapper .ant-btn,
        .action-menu-wrapper a,
        .action-menu-wrapper .anticon,
        .action-menu-wrapper svg,
        .action-menu-wrapper span[role="button"],
        .action-menu-wrapper div[role="button"] {
          margin: 0 !important;
        }

        /* If ActionMenu returns fragment or multiple elements directly */
        .action-menu-wrapper > button + button,
        .action-menu-wrapper > button + .ant-btn,
        .action-menu-wrapper > button + a,
        .action-menu-wrapper > button + span,
        .action-menu-wrapper > button + div,
        .action-menu-wrapper > .ant-btn + button,
        .action-menu-wrapper > .ant-btn + .ant-btn,
        .action-menu-wrapper > .ant-btn + a,
        .action-menu-wrapper > .ant-btn + span,
        .action-menu-wrapper > .ant-btn + div,
        .action-menu-wrapper > a + button,
        .action-menu-wrapper > a + .ant-btn,
        .action-menu-wrapper > a + a,
        .action-menu-wrapper > a + span,
        .action-menu-wrapper > a + div,
        .action-menu-wrapper > span + button,
        .action-menu-wrapper > span + .ant-btn,
        .action-menu-wrapper > span + a,
        .action-menu-wrapper > span + span,
        .action-menu-wrapper > span + div {
          margin-left: 16px !important;
        }

        /* ============================================
           Mobile Cards Styles 
           ============================================ */
        .mobile-cards-wrapper {
          padding: 12px;
          max-height: calc(100vh - 180px);
          overflow-y: auto;
        }

        :global(.mobile-model-card) {
          background: #fff;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        :global(.mobile-model-card:active) {
          transform: scale(0.98);
        }

        .mobile-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f0f0f0;
        }

        .mobile-card-title h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #262626;
          line-height: 1.4;
        }

        .mobile-card-subtitle {
          font-size: 13px;
          color: #8c8c8c;
          margin-top: 2px;
          display: block;
        }

        .mobile-card-action {
          flex-shrink: 0;
          margin-left: 12px;
          display: flex;
          align-items: center;
        }

        .mobile-card-content {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .mobile-card-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 0;
        }

        .mobile-card-label {
          font-size: 13px;
          color: #8c8c8c;
          font-weight: 500;
        }

        .mobile-card-value {
          font-size: 14px;
          color: #262626;
          font-weight: 400;
          text-align: right;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #8c8c8c;
          font-size: 14px;
        }

        /* ============================================
           Table Action Cell Styles 
           ============================================ */
        
        :global(.ant-table-tbody > tr > td.action-menu) {
          text-align: right !important;
          padding-right: 16px !important;
        }

        :global(.ant-table-tbody > tr > td.action-menu .action-menu-wrapper) {
          display: inline-flex !important;
          align-items: center !important;
          gap: 16px !important;
        }

        /* ============================================
           Responsive Adjustments 
           ============================================ */

        /* Mobile specific styles */
        @media (max-width: 767px) {
          .mobile-cards-wrapper {
            padding: 8px;
          }

          :global(.mobile-model-card .ant-card-body) {
            padding: 14px !important;
          }

          .mobile-card-title h3 {
            font-size: 15px;
          }

          .mobile-card-label,
          .mobile-card-value {
            font-size: 13px;
          }

          .action-menu-wrapper {
            gap: 12px !important;
          }

          .action-menu-wrapper > button + button,
          .action-menu-wrapper > button + .ant-btn,
          .action-menu-wrapper > button + a,
          .action-menu-wrapper > .ant-btn + button,
          .action-menu-wrapper > .ant-btn + .ant-btn,
          .action-menu-wrapper > .ant-btn + a,
          .action-menu-wrapper > a + button,
          .action-menu-wrapper > a + .ant-btn,
          .action-menu-wrapper > a + a {
            margin-left: 12px !important;
          }
        }

        /* Tablet styles */
        @media (min-width: 768px) and (max-width: 1023px) {
          :global(.responsive-model-table) {
            font-size: 13px;
          }

          :global(.responsive-model-table .ant-table-thead > tr > th) {
            padding: 12px 8px !important;
            font-size: 13px;
          }

          :global(.responsive-model-table .ant-table-tbody > tr > td) {
            padding: 10px 8px !important;
            font-size: 13px;
          }

          :global(.responsive-model-table .ant-table-cell) {
            white-space: nowrap;
          }

          .action-menu-wrapper {
            gap: 12px !important;
          }
        }

        /* Desktop styles */
        @media (min-width: 1024px) {
          :global(.responsive-model-table .ant-table-thead > tr > th) {
            padding: 14px 16px !important;
          }

          :global(.responsive-model-table .ant-table-tbody > tr > td) {
            padding: 12px 16px !important;
          }

          .action-menu-wrapper {
            gap: 16px !important;
          }
        }

        /* Table scroll improvements for mobile landscape */
        @media (max-width: 1023px) and (orientation: landscape) {
          :global(.responsive-model-table) {
            font-size: 12px;
          }

          :global(.responsive-model-table .ant-table-thead > tr > th),
          :global(.responsive-model-table .ant-table-tbody > tr > td) {
            padding: 8px 6px !important;
          }
        }

        /* Scrollbar styling */
        .mobile-cards-wrapper::-webkit-scrollbar {
          width: 4px;
        }

        .mobile-cards-wrapper::-webkit-scrollbar-track {
          background: #f0f0f0;
        }

        .mobile-cards-wrapper::-webkit-scrollbar-thumb {
          background: #bfbfbf;
          border-radius: 4px;
        }

        .mobile-cards-wrapper::-webkit-scrollbar-thumb:hover {
          background: #8c8c8c;
        }

        /* Tablet table container */
        @media (min-width: 768px) and (max-width: 1023px) {
          .responsive-table-container {
            padding: 0 16px;
          }
        }

        /* Desktop table container */
        @media (min-width: 1024px) {
          .responsive-table-container {
            padding: 0 24px;
          }
        }
      `}</style>
    </>
  );
}